import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { timeout } from 'rxjs/operators'

import { environment } from '../../../../environments/environment'
import { Mission } from '../../interfaces/mission.interface'
import { Sample } from '../../interfaces/sample.interface'
import { Weighing } from '../../interfaces/weighing.interface'
import { ConnectivityService } from './connectivity.service'
import { OfflineCacheService } from './offline-cache.service'
import { SyncQueueService } from './sync-queue.service'

// Timeout for API calls — if no response within this delay, fall back to cache.
// Critical for WebViews where navigator.onLine lies (returns true when actually offline).
// 30s accommodates heavier endpoints under prod data volumes.
const HTTP_TIMEOUT = 30000

@Injectable({
  providedIn: 'root'
})
export class OfflineResourceService {
  private initialized = false
  private initPromise: Promise<void>

  constructor(
    private http: HttpClient,
    private connectivity: ConnectivityService,
    private cache: OfflineCacheService,
    private syncQueue: SyncQueueService
  ) {}

  private async ensureInit(): Promise<void> {
    if (this.initialized) {
      return
    }
    if (!this.initPromise) {
      this.initPromise = this.cache.init().then(() => {
        this.initialized = true
      })
    }
    return this.initPromise
  }

  // --- MISSIONS ---

  async getMissions(): Promise<Mission[]> {
    await this.ensureInit()

    if (this.connectivity.isOnline) {
      try {
        let params = new HttpParams()
        params = params.set('withoutPagination', 'true')
        const missions = await this.http
          .get<Mission[]>(`${environment.apiBaseUrl}missions`, { params })
          .pipe(timeout(HTTP_TIMEOUT))
          .toPromise()

        // Cache all missions — preserve subcategories and fields from previous enriched cache.
        // The list endpoint no longer returns those (lean for prod volumes); show() or warm-up
        // is what backfills them. Without this merge we'd wipe them on every listing.
        for (const mission of missions) {
          const cached = await this.cache.get<Mission>('missions', mission.id)
          if (cached) {
            if (!mission.subcategories && cached.subcategories) {
              mission.subcategories = cached.subcategories
            }
            if (!mission.fields && cached.fields) {
              mission.fields = cached.fields
            }
          }
          await this.cache.put('missions', mission)
        }
        return missions
      } catch (err) {
        // API failed or timed out — mark offline and fall back to cache
        this.connectivity.verifyConnection()
        return this.cache.getAll<Mission>('missions')
      }
    }

    return this.cache.getAll<Mission>('missions')
  }

  /**
   * Merge a mission from the show endpoint into the cache.
   * The list endpoint is now lean (no fields/subcategories) — show() is the
   * source of truth for those. Preserves the list-only counters (sampleCount,
   * weighingCount) when a previous list-endpoint entry is already cached.
   */
  async cacheMission(mission: Mission): Promise<void> {
    await this.ensureInit()
    const cached = await this.cache.get<Mission>('missions', mission.id)
    const merged: Mission = {
      ...(cached || {} as Mission),
      ...mission,
      sampleCount: cached && cached.sampleCount !== undefined ? cached.sampleCount : mission.sampleCount,
      weighingCount: cached && cached.weighingCount !== undefined ? cached.weighingCount : mission.weighingCount
    }
    await this.cache.put('missions', merged)
  }

  // --- SAMPLES ---

  async getSamples(missionId?: number | string): Promise<Sample[]> {
    await this.ensureInit()

    if (this.connectivity.isOnline) {
      try {
        let params = new HttpParams()
        params = params.set('withoutPagination', 'true')
        if (missionId) {
          params = params.set('missionIds', missionId.toString())
        }
        const samples = await this.http
          .get<Sample[]>(`${environment.apiBaseUrl}samples`, { params })
          .pipe(timeout(HTTP_TIMEOUT))
          .toPromise()

        // Clean stale temp entries before caching fresh data
        const cached = await this.getSamplesFromCache(missionId)
        for (const cs of cached) {
          if ((cs as any)._pendingSync) {
            await this.cache.delete('samples', (cs as any).id)
          }
        }

        for (const sample of samples) {
          await this.cache.put('samples', sample)
        }
        return this.sortByCreatedAtDesc(samples)
      } catch (err) {
        this.connectivity.verifyConnection()
        return this.getSamplesFromCache(missionId)
      }
    }

    return this.getSamplesFromCache(missionId)
  }

  async getSample(id: number | string): Promise<Sample> {
    await this.ensureInit()

    // Temp IDs (offline-created) only exist in the local cache — never call the API with them
    if (this.connectivity.isOnline && !this.isTempId(id)) {
      try {
        const sample = await this.http
          .get<Sample>(`${environment.apiBaseUrl}samples/${id}`)
          .pipe(timeout(HTTP_TIMEOUT))
          .toPromise()
        await this.cache.put('samples', sample)
        return sample
      } catch (err) {
        this.connectivity.verifyConnection()
        return this.enrichSampleFromCache(
          await this.cache.get<Sample>('samples', this.parseId(id))
        )
      }
    }

    return this.enrichSampleFromCache(
      await this.cache.get<Sample>('samples', this.parseId(id))
    )
  }

  /**
   * Enriches a cached sample with full mission data (fields, tares, granulometries)
   * from the missions cache. Samples cached from the list endpoint don't include
   * mission.fields or sample.answers — the mission cache (from getMissions()) does.
   */
  private async enrichSampleFromCache(sample: Sample): Promise<Sample> {
    if (!sample || !sample.mission) {
      return sample
    }

    if (!sample.mission.fields || !sample.mission.subcategories) {
      const fullMission = await this.cache.get<Mission>('missions', sample.mission.id)
      if (fullMission) {
        sample.mission = fullMission
      }
    }

    return sample
  }

  // --- WEIGHINGS ---

  async getWeighing(id: number | string): Promise<Weighing> {
    await this.ensureInit()

    // Temp IDs (offline-created) only exist in the local cache — never call the API with them
    if (this.connectivity.isOnline && !this.isTempId(id)) {
      try {
        const weighing = await this.http
          .get<Weighing>(`${environment.apiBaseUrl}weighings/${id}`)
          .pipe(timeout(HTTP_TIMEOUT))
          .toPromise()
        await this.cache.put('weighings', weighing)
        return weighing
      } catch (err) {
        this.connectivity.verifyConnection()
        return this.cache.get<Weighing>('weighings', this.parseId(id))
      }
    }

    return this.cache.get<Weighing>('weighings', this.parseId(id))
  }

  async getWeighings(sampleId: number | string): Promise<Weighing[]> {
    await this.ensureInit()

    // Temp IDs (offline-created) only exist in the local cache — never call the API with them
    if (this.connectivity.isOnline && !this.isTempId(sampleId)) {
      try {
        let params = new HttpParams()
        params = params.set('sampleId', sampleId.toString())
        const weighings = await this.http
          .get<Weighing[]>(`${environment.apiBaseUrl}weighings`, { params })
          .pipe(timeout(HTTP_TIMEOUT))
          .toPromise()

        // Clean stale temp entries for this sample before caching fresh data
        const cached = await this.getWeighingsFromCache(sampleId)
        for (const cw of cached) {
          if ((cw as any)._pendingSync) {
            await this.cache.delete('weighings', (cw as any).id)
          }
        }

        for (const w of weighings) {
          (w as any).sampleId = sampleId
          await this.cache.put('weighings', w)
        }
        return this.sortByCreatedAtDesc(weighings)
      } catch (err) {
        this.connectivity.verifyConnection()
        return this.getWeighingsFromCache(sampleId)
      }
    }

    return this.getWeighingsFromCache(sampleId)
  }

  // --- SAVE SAMPLE ---

  async saveSample(formData: FormData, mode: 'create' | 'update', sampleId?: number | string): Promise<any> {
    await this.ensureInit()

    // Serialize formData for offline storage
    const tempId = this.syncQueue.generateTempId()
    const { payload, photoKeys } = await this.serializeFormData(formData, 'sample', sampleId || tempId)

    if (!this.connectivity.isOnline) {
      const entityId = mode === 'create' ? tempId : sampleId

      // Store in sync queue
      await this.syncQueue.enqueue({
        entityType: 'sample',
        operationType: mode,
        entityId,
        payload,
        photoKeys
      })

      // Optimistic: store sample locally
      const localSample = await this.payloadToSample(payload, entityId)
      await this.cache.put('samples', localSample)

      return localSample
    }

    // Online: direct API call
    if (mode === 'create') {
      return this.http.post(`${environment.apiBaseUrl}samples`, formData).toPromise()
    } else {
      return this.http.put(`${environment.apiBaseUrl}samples/${sampleId}`, formData).toPromise()
    }
  }

  // --- SAVE WEIGHING ---

  async saveWeighing(formData: FormData, mode: 'create' | 'update', sampleId: number | string, weighingId?: number | string): Promise<any> {
    await this.ensureInit()

    const entityIdForPhotos = mode === 'create' ? this.syncQueue.generateTempId() : weighingId
    const { payload, photoKeys } = await this.serializeFormData(formData, 'weighing', entityIdForPhotos)

    if (!this.connectivity.isOnline) {
      const entityId = mode === 'create' ? entityIdForPhotos : weighingId

      await this.syncQueue.enqueue({
        entityType: 'weighing',
        operationType: mode,
        entityId,
        payload,
        photoKeys
      })

      // Optimistic: store weighing locally
      const localWeighing = await this.payloadToWeighing(payload, entityId, sampleId)
      await this.cache.put('weighings', localWeighing)

      return localWeighing
    }

    // Online: direct API call
    if (mode === 'create') {
      return this.http.post(`${environment.apiBaseUrl}weighings`, formData).toPromise()
    } else {
      return this.http.put(`${environment.apiBaseUrl}weighings/${weighingId}`, formData).toPromise()
    }
  }

  // --- CACHE WARM-UP ---

  async warmUpCache(): Promise<void> {
    if (!environment.nativescriptEmbedded || !this.connectivity.isOnline) {
      return
    }

    await this.ensureInit()

    try {
      // Load missions (list endpoint — no fields/subcategories, provides sampleCount/weighingCount)
      const missions = await this.getMissions()

      // Enrich each cached mission with fields + subcategories from show endpoint.
      // The list endpoint no longer ships those (too heavy on prod volumes), so offline
      // consumers (sample-create-edit, payloadToWeighing, enrichSampleFromCache) rely
      // on the warm-up to backfill them.
      for (const mission of missions) {
        try {
          const fullMission = await this.http
            .get<Mission>(`${environment.apiBaseUrl}missions/${mission.id}`)
            .pipe(timeout(HTTP_TIMEOUT))
            .toPromise()
          mission.subcategories = fullMission.subcategories
          mission.fields = fullMission.fields
          await this.cache.put('missions', mission)
        } catch (err) {}
      }

      // Load recent samples (last 10 days)
      const samples = await this.getRecentSamples(10)

      // Load full sample details (with answers) and weighings for each sample
      for (const sample of samples) {
        await this.getSample(sample.id).catch(() => {})
        await this.getWeighings(sample.id).catch(() => {})
      }

      // Cleanup old samples from cache
      await this.cache.cleanupOldSamples(10)

      // Store warm-up date
      await this.cache.put('meta', { key: 'lastSyncDate', value: new Date().toISOString() })
    } catch (err) {
      console.error('Cache warm-up failed:', err)
    }
  }

  // --- WARM-UP HELPERS ---

  private async getRecentSamples(maxAgeDays: number): Promise<Sample[]> {
    await this.ensureInit()

    const dateFrom = new Date()
    dateFrom.setDate(dateFrom.getDate() - maxAgeDays)
    const dateFromStr = dateFrom.toISOString().split('T')[0]

    if (this.connectivity.isOnline) {
      try {
        let params = new HttpParams()
        params = params.set('withoutPagination', 'true')
        params = params.set('dateFrom', dateFromStr)
        const samples = await this.http
          .get<Sample[]>(`${environment.apiBaseUrl}samples`, { params })
          .pipe(timeout(HTTP_TIMEOUT))
          .toPromise()

        // Clean stale temp entries before caching fresh data
        const cached = await this.cache.getAll<Sample>('samples')
        for (const cs of cached) {
          if ((cs as any)._pendingSync) {
            await this.cache.delete('samples', (cs as any).id)
          }
        }

        for (const sample of samples) {
          await this.cache.put('samples', sample)
        }
        return samples
      } catch (err) {
        this.connectivity.verifyConnection()
        return this.cache.getAll<Sample>('samples')
      }
    }

    return this.cache.getAll<Sample>('samples')
  }

  // --- LOCAL DELETE (offline-created entities only) ---

  /**
   * Removes an offline-created entity (temp_xxx id) from the local cache:
   * cache entry, pending sync operation(s), and stored photos.
   * Server entities are not touched — call the API for those.
   */
  async deleteOfflineCreated(entityType: 'sample' | 'weighing', entityId: number | string): Promise<void> {
    await this.ensureInit()

    const store = entityType === 'sample' ? 'samples' : 'weighings'
    await this.cache.delete(store, entityId)

    // Remove pending sync operations targeting this entity
    const allOps = await this.cache.getAll<any>('sync_queue')
    for (const op of allOps) {
      if (op.entityType === entityType && String(op.entityId) === String(entityId)) {
        await this.cache.delete('sync_queue', op.id)
      }
    }

    // Drop stored photos (image1..image8 for samples, image for weighings)
    const fields = entityType === 'sample'
      ? ['image1', 'image2', 'image3', 'image4', 'image5', 'image6', 'image7', 'image8']
      : ['image']
    for (const field of fields) {
      try {
        await this.cache.delete('photos', [entityType, String(entityId), field])
      } catch (e) {}
    }

    // For samples, also remove their offline-created weighings (cascade)
    if (entityType === 'sample') {
      const weighings = await this.cache.getAll<any>('weighings', 'sampleId', IDBKeyRange.only(this.parseId(entityId)))
      for (const w of weighings) {
        if (String(w.id).startsWith('temp_')) {
          await this.deleteOfflineCreated('weighing', w.id)
        }
      }
    }

    // Refresh sync queue counters
    await this.syncQueue.loadPersistedCounts()
  }

  // --- HELPERS ---

  private async getSamplesFromCache(missionId?: number | string): Promise<Sample[]> {
    const samples = missionId
      ? await this.cache.getAll<Sample>('samples', 'missionId', IDBKeyRange.only(this.parseId(missionId)))
      : await this.cache.getAll<Sample>('samples')
    return this.sortByCreatedAtDesc(samples)
  }

  private async getWeighingsFromCache(sampleId: number | string): Promise<Weighing[]> {
    const weighings = await this.cache.getAll<Weighing>('weighings', 'sampleId', IDBKeyRange.only(this.parseId(sampleId)))
    return this.sortByCreatedAtDesc(weighings)
  }

  /**
   * Newest-first ordering (createdAt DESC), matching the API list order
   * (SampleService.index / WeighingService.index both orderBy createdAt DESC).
   * IndexedDB getAll() returns rows by key (id) ascending, so cache reads must be
   * re-sorted to line up with the online lists — and offline-created entities
   * (temp_* string ids, ordered after numeric ids by IndexedDB) end up on top
   * where they belong. Tie-break by numeric id descending.
   */
  private sortByCreatedAtDesc<T>(items: T[]): T[] {
    return (items || []).slice().sort((a: any, b: any) => {
      const ta = a && a.createdAt ? new Date(a.createdAt).getTime() : 0
      const tb = b && b.createdAt ? new Date(b.createdAt).getTime() : 0
      if (tb !== ta) {
        return tb - ta
      }
      const ia = Number(a && a.id)
      const ib = Number(b && b.id)
      if (!isNaN(ia) && !isNaN(ib)) {
        return ib - ia
      }
      return 0
    })
  }

  private isTempId(id: number | string): boolean {
    return typeof id === 'string' && id.startsWith('temp_')
  }

  private parseId(id: number | string): number | string {
    if (typeof id === 'string' && !id.startsWith('temp_')) {
      const parsed = parseInt(id, 10)
      return isNaN(parsed) ? id : parsed
    }
    return id
  }

  /**
   * Retrieves photos stored in IndexedDB for a given entity (e.g. an offline-created sample).
   * Returns a map of fieldName -> Blob (e.g. { image1: Blob, image2: Blob, ... }).
   */
  async getPhotos(entityType: string, entityId: number | string): Promise<{ [fieldName: string]: Blob }> {
    await this.ensureInit()
    const result: { [fieldName: string]: Blob } = {}

    for (let i = 1; i <= 8; i++) {
      const fieldName = 'image' + i
      const blob = await this.cache.getPhoto(entityType, String(entityId), fieldName)
      if (blob) {
        result[fieldName] = blob
      }
    }

    return result
  }

  /**
   * Retrieves a single photo stored in IndexedDB for a given entity and field name.
   */
  async getPhoto(entityType: string, entityId: number | string, fieldName: string): Promise<Blob> {
    await this.ensureInit()
    return this.cache.getPhoto(entityType, String(entityId), fieldName)
  }

  private async serializeFormData(
    formData: FormData,
    entityType: string,
    entityId: number | string
  ): Promise<{ payload: { [key: string]: any }, photoKeys: string[] }> {
    const payload: { [key: string]: any } = {}
    const photoKeys: string[] = []

    formData.forEach((value: any, key: string) => {
      if (value instanceof File || value instanceof Blob) {
        // Store photo in IndexedDB
        const photoKey = `${entityType}|${entityId}|${key}`
        photoKeys.push(photoKey)
        this.cache.putPhoto(entityType, String(entityId), key, value as Blob)
      } else if (payload[key] !== undefined) {
        // Repeated FormData key (e.g. userIds) -> accumulate into an array
        payload[key] = Array.isArray(payload[key])
          ? payload[key].concat(value)
          : [payload[key], value]
      } else {
        payload[key] = value
      }
    })

    return { payload, photoKeys }
  }

  private async payloadToSample(payload: { [key: string]: any }, id: number | string): Promise<any> {
    // Rebuild answers from answerObjects JSON + cached mission fields
    const answers = await this.buildAnswersFromPayload(payload)

    // Resolve mission name + customer from cache so the samples list grid
    // shows Client/Mission columns for offline-created samples.
    // Only copy the fields the grid needs — the cached mission is huge
    // (joined samples, fields, granulometries, ...) and embedding it whole
    // bloats IndexedDB and can crash the rendering.
    const missionId = Number(payload.missionId)
    const cachedMission = await this.cache.get<Mission>('missions', missionId)
    const mission = {
      id: missionId,
      name: cachedMission ? cachedMission.name : '',
      customer: cachedMission ? cachedMission.customer : ''
    } as Mission

    return {
      id,
      name: payload.name || '',
      origin: payload.origin || '',
      weight: Number(payload.weight) || 0,
      tare: Number(payload.tare) || 0,
      missionId,
      mission,
      user: {},
      answers,
      weighings: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      receiptDate: payload.receiptDate || null,
      _pendingSync: true
    }
  }

  private async buildAnswersFromPayload(payload: { [key: string]: any }): Promise<any[]> {
    if (!payload.answerObjects) {
      return []
    }

    try {
      const answerObjects: { fieldId: number, value: string }[] = JSON.parse(payload.answerObjects)
      if (!answerObjects.length) {
        return []
      }

      // Get mission fields from cache to build full Answer objects
      const missionId = Number(payload.missionId)
      const mission = await this.cache.get<any>('missions', missionId)
      const fields: any[] = (mission && mission.fields) || []

      return answerObjects.map(ao => {
        const field = fields.find(f => f.id === ao.fieldId)
        return {
          id: null,
          value: ao.value,
          field: field || { id: ao.fieldId }
        }
      })
    } catch (e) {
      return []
    }
  }

  private async payloadToWeighing(payload: { [key: string]: any }, id: number | string, sampleId: number | string): Promise<any> {
    // Resolve full subcategory (with name) from cached missions
    let subcategory = payload.subcategoryId ? { id: Number(payload.subcategoryId) } as any : null
    if (subcategory) {
      try {
        const sample = await this.cache.get<any>('samples', this.parseId(sampleId))
        if (sample && sample.mission) {
          const mission = await this.cache.get<any>('missions', sample.mission.id)
          if (mission && mission.subcategories) {
            const found = mission.subcategories.find(sc => sc.id === subcategory.id)
            if (found) {
              subcategory = found
            }
          }
        }
      } catch (e) {}
    }

    return {
      id,
      weight: Number(payload.weight) || 0,
      tare: Number(payload.tare) || 0,
      elementCount:
        payload.elementCount !== undefined &&
        payload.elementCount !== null &&
        payload.elementCount !== ''
          ? Number(payload.elementCount)
          : null,
      sampleId,
      sample: { id: sampleId },
      subcategory,
      granulometry: payload.granulometryId ? { id: Number(payload.granulometryId) } : null,
      volume: payload.volume || null,
      comments: payload.comments || null,
      isWeightManual: true,
      createdAt: new Date().toISOString(),
      _pendingSync: true
    }
  }
}
